import {Autocomplete} from '@hconnect/common/components/formFields'
import {AggregationType, KpiOption} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {Box, TextField, Tooltip} from '@mui/material'
import {isArray} from 'lodash'
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
  type RegisterOptions
} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FIELD_WIDTH} from '../../consts'
import {KpiAggregationTypeOption, KpiFormField, KpiUnitOption} from '../../types'

import {ControlledAutocomplete} from '@settings/modules/common/components'
import {AutocompleteOption} from '@settings/modules/common/types'
import {submitOnBlurAndEnterProps} from '@settings/modules/common/utils'

type Config<TFormValues extends FieldValues> = {
  name: Path<TFormValues>
  rules?: Omit<
    RegisterOptions<TFormValues, Path<TFormValues>>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >
}

type KpiFormFieldsProps<TFormValues extends FieldValues> = {
  control: Control<TFormValues>
  config: readonly Config<TFormValues>[]
  submit?: () => Promise<void>
  handleAddCustomUnit: (customUnit: string) => void
  handleAddCustomName?: (customName: string) => void
  handleUnitChange: () => void
  kpiTagname?: string
  units: KpiUnitOption[]
  kpiNames?: KpiOption[]
  equipmentOptions?: KpiOption[]
  visibilityOptions: AutocompleteOption[]
  aggregationTypes: KpiAggregationTypeOption[]
  aggregationType: KpiAggregationTypeOption | null
  weightKpiOptions: {id: string; label: string}[]
}

const getFieldName = <TFormValues extends FieldValues>(
  config: readonly Config<TFormValues>[],
  field: KpiFormField
): Config<TFormValues> | undefined => config.find((c) => c.name === field)

export const KpiFormFields = <TFormValues extends FieldValues>({
  kpiTagname,
  control,
  config,
  handleAddCustomUnit,
  handleUnitChange,
  visibilityOptions,
  submit,
  units,
  aggregationTypes,
  aggregationType,
  weightKpiOptions,
  kpiNames = [],
  equipmentOptions,
  handleAddCustomName
}: KpiFormFieldsProps<TFormValues>) => {
  const {t} = useTranslation()

  const kpiDefinitionIdConfig = getFieldName(config, KpiFormField.KpiDefinitionId)
  const kpiNameConfig = getFieldName(config, KpiFormField.KpiName)
  const kpiMainEquipmentConfig = getFieldName(config, KpiFormField.KpiMainEquipment)
  const kpiTypeConfig = getFieldName(config, KpiFormField.KpiType)
  const unitConfig = getFieldName(config, KpiFormField.Unit)
  const aggregationTypeConfig = getFieldName(config, KpiFormField.AggregationType)
  const weightKpiConfig = getFieldName(config, KpiFormField.WeightKpi)
  const visibilityFlagsConfig = getFieldName(config, KpiFormField.KpiVisibilityFlags)

  return (
    <Box
      {...dataTestId('edit_kpi_form')}
      gap={2}
      sx={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {kpiDefinitionIdConfig && (
        <Controller
          control={control}
          name={kpiDefinitionIdConfig.name}
          render={({field: {ref, value, onChange}, fieldState: {error}}) => (
            <TextField
              label={t('kpiCalculationSettings.label.kpiDefinitionId')}
              sx={{width: FIELD_WIDTH}}
              value={value}
              placeholder={String(value)}
              inputRef={ref}
              variant="filled"
              onChange={onChange}
              helperText={error?.message}
              error={Boolean(error?.message)}
              disabled
              {...dataTestId('kpi_definition_id_input')}
            />
          )}
        />
      )}
      {kpiNameConfig && (
        <Controller
          control={control}
          name={kpiNameConfig.name}
          render={({field: {ref, value, onChange}, fieldState: {error}}) => (
            <TextField
              label={t('kpiCalculationSettings.label.kpiName')}
              sx={{width: FIELD_WIDTH}}
              value={value}
              placeholder={value}
              inputRef={ref}
              variant="filled"
              onChange={onChange}
              helperText={error?.message}
              error={Boolean(error?.message)}
              disabled
              {...dataTestId('kpi_name_input')}
            />
          )}
        />
      )}
      {kpiMainEquipmentConfig &&
        (!equipmentOptions ? (
          <Controller
            control={control}
            name={kpiMainEquipmentConfig.name}
            render={({field: {ref, value, onChange}, fieldState: {error}}) => {
              const kpiEquipment = value.split('-')[0]
              return (
                <TextField
                  label={t('kpiCalculationSettings.label.kpiMainEquipment')}
                  sx={{width: FIELD_WIDTH}}
                  value={kpiEquipment}
                  placeholder={kpiEquipment}
                  inputRef={ref}
                  variant="filled"
                  onChange={onChange}
                  helperText={error?.message}
                  error={Boolean(error?.message)}
                  disabled
                  {...dataTestId('kpi_mainEquipment_input')}
                />
              )
            }}
          />
        ) : (
          <Box sx={{width: FIELD_WIDTH}}>
            <ControlledAutocomplete
              formDataName={kpiMainEquipmentConfig.name}
              label={t('kpiCalculationSettings.label.kpiMainEquipment')}
              control={control}
              options={equipmentOptions}
              groupBy={(option) => option.group ?? ''}
              disabled={equipmentOptions.length === 1}
              onSubmit={submit}
              rules={kpiMainEquipmentConfig.rules}
              {...dataTestId('kpi_name_kpiMainEquipment_input')}
            />
          </Box>
        ))}
      {kpiTypeConfig && (
        <Box sx={{width: FIELD_WIDTH}}>
          <ControlledAutocomplete
            formDataName={kpiTypeConfig.name}
            label={t('kpiCalculationSettings.label.kpiName')}
            control={control}
            options={kpiNames}
            onSubmit={submit}
            onAddCustomValue={handleAddCustomName}
            rules={kpiTypeConfig.rules}
            {...dataTestId('kpi_name_names_input')}
          />
        </Box>
      )}
      {unitConfig && (
        <Box sx={{width: FIELD_WIDTH}}>
          <ControlledAutocomplete
            formDataName={unitConfig.name}
            label={t('kpiCalculationSettings.label.unit')}
            control={control}
            options={units}
            onSubmit={submit}
            onAddCustomValue={handleAddCustomUnit}
            onChangeCb={handleUnitChange}
            rules={unitConfig.rules}
            {...dataTestId('kpi_name_units_input')}
          />
        </Box>
      )}
      {aggregationTypeConfig && (
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box sx={{width: FIELD_WIDTH}}>
            <ControlledAutocomplete
              formDataName={aggregationTypeConfig.name}
              label={t('kpiCalculationSettings.label.aggregationType')}
              control={control}
              options={aggregationTypes}
              getOptionDisabled={(option) => option.disabled}
              onSubmit={submit}
              rules={aggregationTypeConfig.rules}
              {...dataTestId('kpi_aggregation_input')}
            />
          </Box>
          <Tooltip title={t('kpiCalculationSettings.label.mandatoryTooltip')}>
            <ErrorOutlineIcon color="action" sx={{ml: 1}} />
          </Tooltip>
        </Box>
      )}
      {aggregationType?.id === AggregationType.WeightAverage && weightKpiConfig && (
        <Box sx={{width: FIELD_WIDTH}}>
          <ControlledAutocomplete
            getOptionLabel={(option) => option.label}
            formDataName={weightKpiConfig.name}
            label={t('kpiCalculationSettings.label.weightKpi')}
            control={control}
            options={weightKpiOptions.filter((option) => option.label !== kpiTagname)}
            onSubmit={submit}
            rules={weightKpiConfig.rules}
            {...dataTestId('kpi_weight_input')}
          />
        </Box>
      )}
      {visibilityFlagsConfig && (
        <Controller
          control={control}
          name={visibilityFlagsConfig.name}
          render={({field, fieldState}) => (
            <Autocomplete
              {...field}
              value={visibilityOptions.filter(
                (option) => Array.isArray(field.value) && field.value.includes(option.id)
              )}
              label={t('kpiCalculationSettings.label.displayOn')}
              sx={{width: FIELD_WIDTH}}
              options={visibilityOptions}
              getOptionLabel={(option) => option.label}
              multiple
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              onChange={(_, value, reason) => {
                if (isArray(value)) {
                  field.onChange(value.map((v) => v.id))
                }
                if (submit && reason === 'removeOption') {
                  void submit()
                }
              }}
              {...(submit ? submitOnBlurAndEnterProps(submit) : {})}
              {...dataTestId('kpi_visibility_flags_multiselect')}
            />
          )}
        />
      )}
    </Box>
  )
}
